// src/pages/expenses/components/ExpenseModal.jsx
import React, { useState, useEffect } from 'react';
import {
  X,
  Calendar,
  DollarSign,
  FileText,
  Tag,
  CreditCard,
  Check,
  Loader,
  AlertCircle,
} from 'lucide-react';
import { useLocalization } from '../../../contexts/LocalizationContext';
import { useAlert } from '../../../contexts/AlertContext';
import { createExpense, updateExpense } from '../../../services/api/expenses';
import styles from './ExpenseModal.module.css';

const PAYMENT_METHODS = [
  'cash',
  'bank_transfer',
  'credit_card',
  'cheque',
  'mobile_payment',
  'other',
];

const EMPTY_FORM = {
  expenseCategoryId: '',
  subCategoryId: '',
  title: '',
  amount: '',
  expenseDate: new Date().toISOString().split('T')[0],
  paymentMethod: 'cash',
  notes: '',
};

const ExpenseModal = ({ mode, expense, categories, onClose, onSuccess }) => {
  const { t } = useLocalization();
  const { success, error: showError } = useAlert();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [formData, setFormData] = useState(EMPTY_FORM);

  // Sub-categories available based on selected root category
  const [subCategories, setSubCategories] = useState([]);

  // Populate form when editing
  useEffect(() => {
    if (mode === 'edit' && expense) {
      const cat = expense.category;
      const isSubCat = cat && cat.parentId !== null;

      setFormData({
        expenseCategoryId: isSubCat ? String(cat.parentId) : cat ? String(cat.id) : '',
        subCategoryId: isSubCat ? String(cat.id) : '',
        title: expense.title || '',
        amount: expense.amount ? String(expense.amount) : '',
        expenseDate: expense.expenseDate || EMPTY_FORM.expenseDate,
        paymentMethod: expense.paymentMethod || 'cash',
        notes: expense.notes || '',
      });
    } else {
      setFormData(EMPTY_FORM);
    }
  }, [mode, expense]);

  // Sync sub-categories when root category changes
  useEffect(() => {
    if (!formData.expenseCategoryId) {
      setSubCategories([]);
      return;
    }
    const root = categories.find((c) => String(c.id) === formData.expenseCategoryId);
    setSubCategories(root?.children || []);
  }, [formData.expenseCategoryId, categories]);

  const handleChange = (field, value) => {
    setFormData((prev) => {
      const next = { ...prev, [field]: value };
      // Reset sub-category when root changes
      if (field === 'expenseCategoryId') {
        next.subCategoryId = '';
      }
      return next;
    });
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  const validate = () => {
    const newErrors = {};

    const effectiveCategoryId = formData.subCategoryId || formData.expenseCategoryId;
    if (!effectiveCategoryId) {
      newErrors.expenseCategoryId = t('expenses.validation.categoryRequired');
    }

    if (!formData.title.trim()) {
      newErrors.title = t('expenses.validation.titleRequired');
    } else if (formData.title.trim().length < 2) {
      newErrors.title = t('expenses.validation.titleMinLength');
    }

    if (!formData.amount) {
      newErrors.amount = t('expenses.validation.amountRequired');
    } else if (isNaN(formData.amount) || parseFloat(formData.amount) <= 0) {
      newErrors.amount = t('expenses.validation.amountPositive');
    }

    if (!formData.expenseDate) {
      newErrors.expenseDate = t('expenses.validation.dateRequired');
    }

    if (!formData.paymentMethod) {
      newErrors.paymentMethod = t('expenses.validation.paymentMethodRequired');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) {
      showError(t('expenses.validation.fixErrors'));
      return;
    }

    setIsSubmitting(true);

    try {
      const effectiveCategoryId = formData.subCategoryId
        ? parseInt(formData.subCategoryId, 10)
        : parseInt(formData.expenseCategoryId, 10);

      const payload = {
        expenseCategoryId: effectiveCategoryId,
        title: formData.title.trim(),
        amount: parseFloat(formData.amount),
        expenseDate: formData.expenseDate,
        paymentMethod: formData.paymentMethod,
        notes: formData.notes.trim() || null,
      };

      if (mode === 'edit') {
        await updateExpense(expense.id, payload);
        success(t('expenses.expenseUpdatedSuccessfully'));
      } else {
        await createExpense(payload);
        success(t('expenses.expenseCreatedSuccessfully'));
      }

      onSuccess();
    } catch (err) {
      console.error('Error saving expense:', err);
      showError(
        err.response?.data?.message ||
          t(mode === 'edit' ? 'expenses.failedToUpdateExpense' : 'expenses.failedToCreateExpense')
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className={styles.modalOverlay} onClick={(e) => { if (e.target === e.currentTarget && !isSubmitting) onClose(); }}>
      <div className={styles.modalContent}>
        {/* Header */}
        <div className={styles.modalHeader}>
          <h2>
            {mode === 'edit' ? t('expenses.editExpense') : t('expenses.recordNewExpense')}
          </h2>
          <button onClick={onClose} className={styles.closeButton} disabled={isSubmitting}>
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} className={styles.modalBody}>
          <div className={styles.formGrid}>

            {/* Root Category */}
            <div className={styles.formGroup}>
              <label htmlFor="expenseCategoryId">
                {t('expenses.category')} *
              </label>
              <div className={styles.inputWrapper}>
                <Tag className={styles.inputIcon} size={18} />
                <select
                  id="expenseCategoryId"
                  value={formData.expenseCategoryId}
                  onChange={(e) => handleChange('expenseCategoryId', e.target.value)}
                  className={errors.expenseCategoryId ? styles.error : ''}
                  disabled={isSubmitting}
                >
                  <option value="">{t('expenses.selectCategory')}</option>
                  {categories.map((cat) => (
                    <option key={cat.id} value={String(cat.id)}>
                      {cat.name}
                    </option>
                  ))}
                </select>
              </div>
              {errors.expenseCategoryId && (
                <span className={styles.errorText}>
                  <AlertCircle size={12} />
                  {errors.expenseCategoryId}
                </span>
              )}
            </div>

            {/* Sub-Category (shown only when root has children) */}
            <div className={styles.formGroup}>
              <label htmlFor="subCategoryId">
                {t('expenses.subCategory')}
              </label>
              <div className={styles.inputWrapper}>
                <Tag className={styles.inputIcon} size={18} />
                <select
                  id="subCategoryId"
                  value={formData.subCategoryId}
                  onChange={(e) => handleChange('subCategoryId', e.target.value)}
                  disabled={isSubmitting || subCategories.length === 0}
                >
                  <option value="">{t('expenses.selectSubCategory')}</option>
                  {subCategories.map((sub) => (
                    <option key={sub.id} value={String(sub.id)}>
                      {sub.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Title (full width) */}
            <div className={`${styles.formGroup} ${styles.fullWidth}`}>
              <label htmlFor="title">
                {t('expenses.title')} *
              </label>
              <div className={styles.inputWrapper}>
                <FileText className={styles.inputIcon} size={18} />
                <input
                  type="text"
                  id="title"
                  value={formData.title}
                  onChange={(e) => handleChange('title', e.target.value)}
                  placeholder={t('expenses.enterTitle')}
                  className={errors.title ? styles.error : ''}
                  disabled={isSubmitting}
                />
              </div>
              {errors.title && (
                <span className={styles.errorText}>
                  <AlertCircle size={12} />
                  {errors.title}
                </span>
              )}
            </div>

            {/* Amount */}
            <div className={styles.formGroup}>
              <label htmlFor="amount">
                {t('expenses.amount')} *
              </label>
              <div className={styles.inputWrapper}>
                <DollarSign className={styles.inputIcon} size={18} />
                <input
                  type="number"
                  id="amount"
                  value={formData.amount}
                  onChange={(e) => handleChange('amount', e.target.value)}
                  placeholder={t('expenses.enterAmount')}
                  min="0.01"
                  step="0.01"
                  className={errors.amount ? styles.error : ''}
                  disabled={isSubmitting}
                />
              </div>
              {errors.amount && (
                <span className={styles.errorText}>
                  <AlertCircle size={12} />
                  {errors.amount}
                </span>
              )}
            </div>

            {/* Expense Date */}
            <div className={styles.formGroup}>
              <label htmlFor="expenseDate">
                {t('expenses.expenseDate')} *
              </label>
              <div className={styles.inputWrapper}>
                <Calendar className={styles.inputIcon} size={18} />
                <input
                  type="date"
                  id="expenseDate"
                  value={formData.expenseDate}
                  onChange={(e) => handleChange('expenseDate', e.target.value)}
                  className={errors.expenseDate ? styles.error : ''}
                  disabled={isSubmitting}
                />
              </div>
              {errors.expenseDate && (
                <span className={styles.errorText}>
                  <AlertCircle size={12} />
                  {errors.expenseDate}
                </span>
              )}
            </div>

            {/* Payment Method */}
            <div className={styles.formGroup}>
              <label htmlFor="paymentMethod">
                {t('expenses.paymentMethod')} *
              </label>
              <div className={styles.inputWrapper}>
                <CreditCard className={styles.inputIcon} size={18} />
                <select
                  id="paymentMethod"
                  value={formData.paymentMethod}
                  onChange={(e) => handleChange('paymentMethod', e.target.value)}
                  className={errors.paymentMethod ? styles.error : ''}
                  disabled={isSubmitting}
                >
                  {PAYMENT_METHODS.map((key) => (
                    <option key={key} value={key}>
                      {t(`expenses.paymentMethods.${key}`)}
                    </option>
                  ))}
                </select>
              </div>
              {errors.paymentMethod && (
                <span className={styles.errorText}>
                  <AlertCircle size={12} />
                  {errors.paymentMethod}
                </span>
              )}
            </div>

            {/* Notes (full width) */}
            <div className={`${styles.formGroup} ${styles.fullWidth}`}>
              <label htmlFor="notes">
                {t('expenses.notes')}
              </label>
              <div className={styles.inputWrapper}>
                <FileText className={styles.inputIcon} size={18} />
                <textarea
                  id="notes"
                  value={formData.notes}
                  onChange={(e) => handleChange('notes', e.target.value)}
                  placeholder={t('expenses.enterNotes')}
                  rows={3}
                  disabled={isSubmitting}
                />
              </div>
            </div>
          </div>
        </form>

        {/* Footer */}
        <div className={styles.modalFooter}>
          <button
            type="button"
            onClick={onClose}
            className={styles.cancelButton}
            disabled={isSubmitting}
          >
            {t('common.cancel')}
          </button>
          <button
            type="submit"
            onClick={handleSubmit}
            className={styles.submitButton}
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <Loader className={styles.buttonSpinner} size={18} />
                {mode === 'edit' ? t('common.updating') : t('common.creating')}
              </>
            ) : (
              <>
                <Check size={18} />
                {mode === 'edit' ? t('expenses.updateExpense') : t('expenses.createExpense')}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ExpenseModal;
